import {
  CrudFieldConfig,
  CrudFieldContext,
  CrudFieldValidator,
  UploadableFile,
  UploadableImage,
} from "./types";
import { getValueAtPath, isFileLike, resolveFieldState, setValueAtPath } from "./utils";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[+()\-.\s\d]{7,20}$/;
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isPopulatedString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

export const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "boolean") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (isFileLike(value)) {
    return true;
  }

  if (typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return true;
};

export const isValidEmail = (value: string) => emailPattern.test(value.trim());

export const isValidPhoneNumber = (value: string) => phonePattern.test(value.trim());

export const isValidSlug = (value: string) => slugPattern.test(value.trim());

export const isValidUuid = (value: string) => uuidPattern.test(value.trim());

export const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(value.trim());
    return ["http:", "https:"].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

export const isValidAssetReference = (value?: UploadableImage | UploadableFile) => {
  if (!value) {
    return false;
  }

  if (isFileLike(value)) {
    return true;
  }

  if (typeof value !== "string") {
    return false;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  if (
    trimmed.startsWith("/") ||
    /^(uploads|rails|storage|active_storage)\b/i.test(trimmed) ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("data:")
  ) {
    return true;
  }

  return isValidHttpUrl(trimmed);
};

export const isValidHexColor = (value: string) =>
  /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());

export const isValidDateValue = (value: string) => {
  const trimmed = value.trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return false;
  }

  const date = new Date(`${trimmed}T00:00:00`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === trimmed;
};

export const isValidDateTimeLocalValue = (value: string) => {
  const trimmed = value.trim();

  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return false;
  }

  const date = new Date(trimmed);
  return Number.isFinite(date.getTime());
};

export const buildOptionalHttpUrlValidator =
  (label: string): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isPopulatedString(value) && isValidHttpUrl(value)
      ? undefined
      : `${label} must be a valid URL.`;
  };

export const buildOptionalEmailValidator =
  (label: string): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isPopulatedString(value) && isValidEmail(value)
      ? undefined
      : `${label} must be a valid email address.`;
  };

export const buildOptionalPhoneValidator =
  (label: string): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isPopulatedString(value) && isValidPhoneNumber(value)
      ? undefined
      : `${label} must be a valid phone number.`;
  };

export const buildOptionalHexColorValidator =
  (label: string): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isPopulatedString(value) && isValidHexColor(value)
      ? undefined
      : `${label} must be a valid hex color.`;
  };

export const buildSlugValidator =
  (label = "Slug"): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isPopulatedString(value) && isValidSlug(value)
      ? undefined
      : `${label} can only contain lowercase letters, numbers, and hyphens.`;
  };

export const buildOptionalAssetValidator =
  (label: string): CrudFieldValidator =>
  (value) => {
    if (!hasMeaningfulValue(value)) {
      return undefined;
    }

    return isValidAssetReference(value)
      ? undefined
      : `${label} must be an upload or a valid asset URL.`;
  };

export const buildMinMaxValidator =
  ({
    label,
    otherField,
    relation,
  }: {
    label: string;
    otherField: string;
    relation: "gte" | "lte";
  }): CrudFieldValidator =>
  (value, values) => {
    if (!hasMeaningfulValue(value) || !hasMeaningfulValue(getValueAtPath(values, otherField))) {
      return undefined;
    }

    const current = Number(value);
    const other = Number(getValueAtPath(values, otherField));

    if (!Number.isFinite(current) || !Number.isFinite(other)) {
      return undefined;
    }

    if (relation === "gte" && current < other) {
      return `${label} must be greater than or equal to the related value.`;
    }

    if (relation === "lte" && current > other) {
      return `${label} must be less than or equal to the related value.`;
    }

    return undefined;
  };

const maybeValidateByConvention = (field: CrudFieldConfig, value: unknown) => {
  if (!hasMeaningfulValue(value) || typeof value !== "string") {
    return undefined;
  }

  const normalizedName = field.name.toLowerCase();

  if (normalizedName === "slug") {
    return isValidSlug(value)
      ? undefined
      : `${field.label} can only contain lowercase letters, numbers, and hyphens.`;
  }

  if (
    normalizedName === "website" ||
    normalizedName.endsWith("_url") ||
    normalizedName === "video_url"
  ) {
    return isValidHttpUrl(value) ? undefined : `${field.label} must be a valid URL.`;
  }

  if (normalizedName === "email" || normalizedName.endsWith("_email")) {
    return isValidEmail(value) ? undefined : `${field.label} must be a valid email address.`;
  }

  if (normalizedName.endsWith("_color")) {
    return isValidHexColor(value) ? undefined : `${field.label} must be a valid hex color.`;
  }

  return undefined;
};

const validatePrimitiveField = (field: CrudFieldConfig, value: unknown) => {
  if (!hasMeaningfulValue(value)) {
    return undefined;
  }

  if (field.kind === "email" && typeof value === "string" && !isValidEmail(value)) {
    return `${field.label} must be a valid email address.`;
  }

  if (field.kind === "number") {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return `${field.label} must be a valid number.`;
    }

    if (typeof field.min === "number" && numericValue < field.min) {
      return `${field.label} must be at least ${field.min}.`;
    }

    if (typeof field.max === "number" && numericValue > field.max) {
      return `${field.label} must be no more than ${field.max}.`;
    }
  }

  if (field.kind === "date" && typeof value === "string" && !isValidDateValue(value)) {
    return `${field.label} must be a valid date.`;
  }

  if (
    field.kind === "datetime-local" &&
    typeof value === "string" &&
    !isValidDateTimeLocalValue(value)
  ) {
    return `${field.label} must be a valid date and time.`;
  }

  if (
    (field.kind === "image" || field.kind === "file") &&
    !isValidAssetReference(value as UploadableImage | UploadableFile)
  ) {
    return `${field.label} must be an upload or a valid asset URL.`;
  }

  return maybeValidateByConvention(field, value);
};

const validateCrudField = (
  errors: Record<string, any>,
  field: CrudFieldConfig,
  values: Record<string, any>,
  context?: CrudFieldContext,
) => {
  if (resolveFieldState(field.hidden, values)) {
    return errors;
  }

  const value = getValueAtPath(values, field.name);
  const isMissingRequired = field.required && !hasMeaningfulValue(value);

  if (isMissingRequired) {
    return setValueAtPath(errors, field.name, `${field.label} is required.`);
  }

  const primitiveValidationError = validatePrimitiveField(field, value);

  if (primitiveValidationError) {
    return setValueAtPath(errors, field.name, primitiveValidationError);
  }

  if (field.kind === "object_array") {
    const items = Array.isArray(value) ? value : [];

    if (field.required && items.length === 0) {
      return setValueAtPath(errors, field.name, `Add at least one ${field.label.toLowerCase()}.`);
    }

    const collectionValidationError = field.validate?.(items, values, context);

    if (collectionValidationError) {
      return setValueAtPath(errors, field.name, collectionValidationError);
    }

    return items.reduce<Record<string, any>>((nextErrors, item, index) => {
      return (field.itemFields || []).reduce<Record<string, any>>((nestedErrors, nestedField) => {
        return validateCrudField(
          nestedErrors,
          { ...nestedField, name: `${field.name}.${index}.${nestedField.name}` },
          values,
          { index, item },
        );
      }, nextErrors);
    }, errors);
  }

  const customValidationError = field.validate?.(value, values, context);

  if (customValidationError) {
    return setValueAtPath(errors, field.name, customValidationError);
  }

  return errors;
};

export const validateCrudValues = (fields: CrudFieldConfig[], values: Record<string, any>) =>
  fields.reduce<Record<string, any>>(
    (errors, field) => validateCrudField(errors, field, values),
    {},
  );
